import React from 'react';
import { observer } from 'mobx-react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { injectTSDI } from '../core/tsdi';
import { FormSection } from '../components/form-section';
import { PageHeader } from '../components/page-header';
import { Option, Select } from '../components/select';
import { StickyBar } from '../components/sticky-bar';
import { TextField } from '../components/textfield';
import { InitialData } from '../core/initial-data';
import { CreateTeamsTenantDto, TenantType } from '../core/api/TenantClient';
import { useStore } from './store';

function getTenantTypeOption(): Option<CreateTeamsTenantDto['tenantType']>[] {
    return [
        { value: TenantType.CLIENT, label: 'Client' },
        { value: TenantType.DEMO, label: 'Demo' },
        { value: TenantType.PARTNER, label: 'Partner' },
        { value: TenantType.UNMANAGED, label: 'Unmanaged' }
    ];
}

export const TenantOnboarding = observer(() => {
    const { fields, onSubmitHandler, errors, tenantRequest, onCancelHandler } =
        useStore();
    const { availableCountries } = injectTSDI(InitialData);

    return (
        <div className="mx-auto w-full max-w-[900px]">
            <PageHeader
                title="Tenant Onboarding"
                description="Create a new tenant with its company, admin and configuration"
            />

            <div className="flex flex-col gap-6 pb-24">
                <FormSection
                    title="Tenant"
                    description="Identity, type and licensing"
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="md:col-span-2">
                            <TextField
                                type="text"
                                label="Tenant Id"
                                value={fields.tenantId.value}
                                onChange={fields.tenantId.onChange}
                                error={fields.tenantId.error}
                                required
                            />
                        </div>
                        <Select
                            label="Tenant Type"
                            value={fields.tenantType.value}
                            onChange={fields.tenantType.onChange}
                            error={fields.tenantType.error}
                            options={getTenantTypeOption()}
                            required
                        />
                        <TextField
                            type="number"
                            label="Licenses"
                            value={fields.licensesCount.value}
                            onChange={fields.licensesCount.onChange}
                            error={fields.licensesCount.error}
                        />
                        <div className="md:col-span-2">
                            <TextField
                                type="text"
                                label="Company Name"
                                value={fields.company.name.value}
                                onChange={fields.company.name.onChange}
                                error={fields.company.name.error}
                                required
                            />
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Address" description="Company address">
                    <div className="grid gap-4 md:grid-cols-4">
                        <div className="md:col-span-3">
                            <TextField
                                type="text"
                                label="Street"
                                required
                                value={fields.company.address.street.value}
                                onChange={
                                    fields.company.address.street.onChange
                                }
                                error={fields.company.address.street.error}
                            />
                        </div>
                        <TextField
                            type="text"
                            label="Number"
                            required
                            value={fields.company.address.houseNumber.value}
                            onChange={
                                fields.company.address.houseNumber.onChange
                            }
                            error={fields.company.address.houseNumber.error}
                        />
                        <TextField
                            type="text"
                            label="Post Code"
                            required
                            value={fields.company.address.zip.value}
                            onChange={fields.company.address.zip.onChange}
                            error={fields.company.address.zip.error}
                        />
                        <div className="md:col-span-3">
                            <TextField
                                type="text"
                                label="City"
                                value={fields.company.address.city.value}
                                onChange={fields.company.address.city.onChange}
                                error={fields.company.address.city.error}
                                required
                            />
                        </div>
                        <div className="md:col-span-4">
                            <Select
                                label="Country"
                                value={fields.company.address.country.value}
                                onChange={
                                    fields.company.address.country.onChange
                                }
                                error={fields.company.address.country.error}
                                options={availableCountries.$}
                                required
                            />
                        </div>
                    </div>
                </FormSection>

                <FormSection
                    title="Configuration"
                    description="Domains, authentication and headcount"
                >
                    <div className="grid gap-4">
                        <TextField
                            type="text"
                            label="Accept Domains"
                            placeholder="Seperated by commas"
                            value={
                                fields.company.companyConfiguration
                                    .acceptedDomains.value
                            }
                            onChange={
                                fields.company.companyConfiguration
                                    .acceptedDomains.onChange
                            }
                        />
                        <TextField
                            type="text"
                            label="Authentication Token"
                            value={
                                fields.company.companyConfiguration
                                    .authenticationToken.value
                            }
                            onChange={
                                fields.company.companyConfiguration
                                    .authenticationToken.onChange
                            }
                        />
                        <TextField
                            type="number"
                            label="Declared employees count"
                            value={
                                fields.company.companyConfiguration
                                    .declaredEmployeesCount.value
                            }
                            onChange={
                                fields.company.companyConfiguration
                                    .declaredEmployeesCount.onChange
                            }
                        />
                    </div>
                </FormSection>

                <FormSection
                    title="Company Admin"
                    description="The first administrator of this tenant"
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            type="text"
                            label="First Name"
                            value={fields.companyAdmin.firstName.value}
                            onChange={fields.companyAdmin.firstName.onChange}
                            error={fields.companyAdmin.firstName.error}
                            helperText
                            required
                        />
                        <TextField
                            type="text"
                            label="Last Name"
                            value={fields.companyAdmin.lastName.value}
                            onChange={fields.companyAdmin.lastName.onChange}
                            error={fields.companyAdmin.firstName.error}
                            helperText
                            required
                        />
                        <div className="md:col-span-2">
                            <TextField
                                type="text"
                                label="Email"
                                value={fields.companyAdmin.email.value}
                                onChange={fields.companyAdmin.email.onChange}
                                error={fields.companyAdmin.email.error}
                                helperText
                                required
                            />
                        </div>
                    </div>
                </FormSection>

                <FormSection
                    title="Links and whitelabel"
                    description="External references and whitelabel setup"
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="md:col-span-2">
                            <TextField
                                type="text"
                                label="Lexoffice Link"
                                value={fields.lexofficeLink.value}
                                onChange={fields.lexofficeLink.onChange}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <TextField
                                type="text"
                                label="Hubspot Link"
                                value={fields.hubspotLink.value}
                                onChange={fields.hubspotLink.onChange}
                            />
                        </div>
                        <TextField
                            type="text"
                            label="Whitelabel ID"
                            value={fields.whitelabelId.value}
                            onChange={fields.whitelabelId.onChange}
                        />
                        <TextField
                            type="text"
                            label="Whitelabel CurrencyCode"
                            value={fields.whitelabelCurrencyCode.value}
                            onChange={fields.whitelabelCurrencyCode.onChange}
                        />
                    </div>
                </FormSection>

                {tenantRequest.error?.message && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {tenantRequest.error?.message}
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            <StickyBar>
                <Button onClick={onCancelHandler} variant="ghost">
                    Cancel
                </Button>
                <Button
                    loading={tenantRequest.loading}
                    onClick={onSubmitHandler}
                    disabled={Boolean(errors) || tenantRequest.loading}
                >
                    Submit
                </Button>
            </StickyBar>
        </div>
    );
});
